import Modal from '@components/Modal'
import { backgroundOrange, orange } from '@/theme/colors'
import { useJoinTrip } from '@hooks/useJoinTrip'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import {
	ButtonComponent,
	ButtonGroupComponent,
	FormInputComponent,
} from '../../Itinerary/AddEventModal/FormComponents'

interface JoinTripModalProps {
	isOpen: boolean
	onClose: () => void
}

const JoinTripModal: React.FC<JoinTripModalProps> = ({ isOpen, onClose }) => {
	const [code, setCode] = useState('')
	const [error, setError] = useState<string | null>(null)
	const joinTrip = useJoinTrip()

	const validateCode = (value: string): string | null => {
		const trimmed = value.trim().toUpperCase()

		if (trimmed.length === 0) {
			return null // No error for empty input
		}

		// Check if contains only alphabetic characters
		if (!/^[A-Z]+$/.test(trimmed)) {
			return 'Trip code must contain only letters'
		}

		// Check length
		if (trimmed.length !== 6) {
			return 'Trip code must be exactly 6 characters'
		}

		return null
	}

	const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.toUpperCase()
		// Only allow alphabetic characters
		const alphabeticOnly = value.replace(/[^A-Z]/gi, '')
		// Limit to 6 characters
		const limited = alphabeticOnly.slice(0, 6)

		setCode(limited)
		setError(validateCode(limited))
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const trimmedCode = code.trim().toUpperCase()

		if (!trimmedCode) {
			setError('Trip code is required')
			return
		}

		const validationError = validateCode(trimmedCode)
		if (validationError) {
			setError(validationError)
			return
		}

		try {
			await joinTrip.mutateAsync(trimmedCode)
			toast.success('Successfully joined the trip!')
			setCode('')
			setError(null)
			onClose()
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message || error?.message || 'Failed to join trip. Please check the code and try again.'
			toast.error(errorMessage)
			console.error('Error joining trip:', error)
		}
	}

	const handleCancel = () => {
		setCode('')
		setError(null)
		onClose()
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Join an Existing Trip"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			closeButtonColor={orange}
		>
			<form onSubmit={handleSubmit}>
				<FormInputComponent
					label="Trip Code"
					htmlFor="trip-code"
					id="trip-code"
					type="text"
					value={code}
					onChange={handleCodeChange}
					placeholder="Enter 6-letter code..."
					required
					autoFocus
					maxLength={6}
					style={error ? { borderColor: '#dc2626' } : undefined}
				/>
				{error && (
					<div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
						{error}
					</div>
				)}
				{!error && code.length > 0 && code.length < 6 && (
					<div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
						{6 - code.length} character{6 - code.length !== 1 ? 's' : ''} remaining
					</div>
				)}
				<ButtonGroupComponent>
					<ButtonComponent type="button" variant="secondary" onClick={handleCancel}>
						Cancel
					</ButtonComponent>
					<ButtonComponent
						type="submit"
						variant="primary"
						disabled={!code.trim() || code.length !== 6 || !!error || joinTrip.isPending}
					>
						Join Trip
					</ButtonComponent>
				</ButtonGroupComponent>
			</form>
		</Modal>
	)
}

export default JoinTripModal

